import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { fetchPreferences } from '../../hooks/usePreferences';
import { firstAllowedPage } from '../../lib/permissions';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AuthResponse, authService, LoginDto, needsTwoFactor } from '../../services/auth.service';
import { useAuthStore } from '../../store/auth.store';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  // Set once the password was right on an account with two-step sign-in: the code is asked next
  const [challenge, setChallenge] = useState<string | null>(null);
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginDto>();

  const finish = (data: AuthResponse) => {
      setUser(data.user);
      toast.success('Login successful!');
      // Open on the page the user chose in Settings; the dashboard when that cannot be read
      // ...or the first page the role allows, when that one is not open to this user
      const permissions = (data.user as { permissions?: string[] }).permissions;
      if (data.user.portal) {
        navigate('/portal');
        return;
      }
      fetchPreferences()
        .then((preferences) => navigate(firstAllowedPage(permissions, preferences.startPage || '/dashboard')))
        .catch(() => navigate(firstAllowedPage(permissions, '/dashboard')));
  };

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      if (needsTwoFactor(data)) {
        setChallenge(data.challengeToken);
        setUseRecovery(false);
        setCode('');
        setCodeError('');
        return;
      }
      finish(data);
    },
    onError: (error: any) => {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Login failed');
    },
  });

  const onSubmit = (data: LoginDto) => {
    loginMutation.mutate(data);
  };

  const verifyMutation = useMutation({
    mutationFn: authService.verifyTwoFactor,
    onSuccess: finish,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'The code could not be checked';
      setCode('');
      // An expired sign-in starts again from the password
      if (/expired/i.test(String(message))) {
        setChallenge(null);
        toast.error(String(message));
        return;
      }
      setCodeError(Array.isArray(message) ? message.join(', ') : String(message));
    },
  });

  const submitCode = (event: React.FormEvent) => {
    event.preventDefault();
    const value = code.trim();
    const valid = useRecovery ? value.replace(/[\s-]/g, '').length === 8 : /^\d{6}$/.test(value.replace(/\s/g, ''));
    if (!valid) {
      setCodeError(useRecovery ? 'Enter one of your recovery codes, like ABCD-EFGH' : 'Enter the six digits the authenticator app shows');
      return;
    }
    setCodeError('');
    const challengeToken = challenge as string;
    verifyMutation.mutate(useRecovery ? { challengeToken, recoveryCode: value } : { challengeToken, code: value.replace(/\s/g, '') });
  };

  if (challenge) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <form onSubmit={submitCode} noValidate className="max-w-md w-full space-y-6 rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <div>
            <h2 className="text-center text-2xl font-extrabold text-gray-900">Two-step sign-in</h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {useRecovery
                ? 'Enter one of the recovery codes you saved when you turned two-step sign-in on. Each one works once.'
                : 'Open the authenticator app on your phone and enter the six-digit code it shows for RightFitERP.'}
            </p>
          </div>
          <div>
            <label htmlFor="two-factor-code" className="block text-sm font-medium text-gray-700">
              {useRecovery ? 'Recovery code' : 'Code'}
            </label>
            <input
              id="two-factor-code"
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                setCodeError('');
              }}
              autoFocus
              autoComplete="one-time-code"
              inputMode={useRecovery ? 'text' : 'numeric'}
              maxLength={useRecovery ? 12 : 7}
              placeholder={useRecovery ? 'ABCD-EFGH' : '123456'}
              aria-invalid={!!codeError}
              className={`mt-1 block w-full rounded-md border px-3 py-2 text-center font-mono text-lg tracking-widest text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${codeError ? 'border-red-400' : 'border-gray-300'}`}
            />
            {codeError && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                {codeError}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={verifyMutation.isPending}
            className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {verifyMutation.isPending ? 'Checking…' : 'Verify'}
          </button>
          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => {
                setUseRecovery(!useRecovery);
                setCode('');
                setCodeError('');
              }}
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {useRecovery ? 'Use the authenticator app' : 'Use a recovery code'}
            </button>
            <button type="button" onClick={() => setChallenge(null)} className="text-gray-500 hover:text-gray-700">
              Back
            </button>
          </div>
          <p className="text-xs text-gray-500">Lost your phone and your recovery codes? An administrator can turn two-step sign-in off for you on the Users page.</p>
        </form>
      </div>
    );
  }

  const fillTestCredentials = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link to="/register" className="font-medium text-indigo-600 hover:text-indigo-500">
              create a new account
            </Link>
          </p>
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="text-sm font-medium text-blue-800">Test Credentials (click to fill):</h3>
            <div className="mt-2 space-y-1 text-xs text-blue-600">
              <button
                type="button"
                onClick={() => fillTestCredentials('admin@erp.com', 'admin123')}
                className="block w-full text-left hover:bg-blue-100 rounded p-1 transition-colors"
              >
                <strong>Admin:</strong> admin@erp.com / admin123
              </button>
              <button
                type="button"
                onClick={() => fillTestCredentials('manager@erp.com', 'manager123')}
                className="block w-full text-left hover:bg-blue-100 rounded p-1 transition-colors"
              >
                <strong>Manager:</strong> manager@erp.com / manager123
              </button>
              <button
                type="button"
                onClick={() => fillTestCredentials('user@erp.com', 'user123')}
                className="block w-full text-left hover:bg-blue-100 rounded p-1 transition-colors"
              >
                <strong>User:</strong> user@erp.com / user123
              </button>
            </div>
          </div>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                id="email"
                type="email"
                autoComplete="email"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                {...register('password', { required: 'Password is required' })}
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                <span className="text-gray-500 text-sm">
                  {showPassword ? 'Hide' : 'Show'}
                </span>
              </button>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loginMutation.isPending ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;